import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 60);
    }
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
        setMobileCatOpen(false);
      }
    }
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6 py-4 flex items-center justify-between">
        <Link
          to="/"
          className={`text-xl font-heading font-semibold whitespace-nowrap cursor-pointer ${
            scrolled || mobileMenuOpen ? 'text-foreground-900' : 'text-white'
          }`}
        >
          GTBuySheet
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              scrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
            }`}
          >
            Home
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
              }`}
            >
              Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-lg py-2 max-h-80 overflow-y-auto shadow-lg border border-background-200/70">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/article/shopping-guide-kaspreadsheet"
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              scrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
            }`}
          >
            Shopping Guide
          </Link>

          <Link
            to="/article/gtbuy-spreadsheet-guide"
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              scrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
            }`}
          >
            Guides
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
          </a>

          {/* Mobile menu toggle */}
          <button
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
              setMobileCatOpen(false);
            }}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-full cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line text-foreground-900' : (scrolled ? 'ri-menu-line text-foreground-900' : 'ri-menu-line text-white')}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div ref={mobileRef} className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-3 text-foreground-700 hover:bg-background-100 rounded-lg text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
              Home
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-3 py-3 text-foreground-700 hover:bg-background-100 rounded-lg text-sm font-medium transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line w-5 h-5 flex items-center justify-center"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-4 pl-3 border-l border-background-200/70 space-y-1">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between px-3 py-2.5 text-foreground-600 hover:bg-background-100 rounded-lg text-sm transition-colors cursor-pointer"
                  >
                    <span className="whitespace-nowrap">{cat.name}</span>
                    <i className="ri-external-link-line text-foreground-400 w-4 h-4 flex items-center justify-center"></i>
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/article/shopping-guide-kaspreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-3 text-foreground-700 hover:bg-background-100 rounded-lg text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-shopping-bag-line w-5 h-5 flex items-center justify-center"></i>
              Shopping Guide
            </Link>

            <Link
              to="/article/gtbuy-spreadsheet-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-3 text-foreground-700 hover:bg-background-100 rounded-lg text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line w-5 h-5 flex items-center justify-center"></i>
              Guides
            </Link>

            <div className="pt-3 mt-3 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}